import { Position } from "@xyflow/react";
import { GEOMETRY } from "./canvas-geometry";

export type Pt = { x: number; y: number };

const { STUB } = GEOMETRY;

export function sideDir(p: Position): Pt {
  switch (p) {
    case Position.Top:
      return { x: 0, y: -1 };
    case Position.Bottom:
      return { x: 0, y: 1 };
    case Position.Left:
      return { x: -1, y: 0 };
    case Position.Right:
    default:
      return { x: 1, y: 0 };
  }
}

/** Orthogonal polyline that always starts at `source` and ends at `target`. */
export function routeOrthogonal(
  source: Pt,
  target: Pt,
  sourceSide: Position,
  targetSide: Position,
): Pt[] {
  const sDir = sideDir(sourceSide);
  const tDir = sideDir(targetSide);
  const sa: Pt = { x: source.x + sDir.x * STUB, y: source.y + sDir.y * STUB };
  const ta: Pt = { x: target.x + tDir.x * STUB, y: target.y + tDir.y * STUB };
  const sHoriz = sDir.x !== 0;
  const tHoriz = tDir.x !== 0;

  // One bend in every case — a centred Z leaves a visible step when the ends
  // aren't aligned on the cross-axis.
  let bends: Pt[];
  if (sHoriz && tHoriz) {
    bends = [{ x: sa.x, y: ta.y }];
  } else if (!sHoriz && !tHoriz) {
    bends = [{ x: ta.x, y: sa.y }];
  } else if (sHoriz && !tHoriz) {
    bends = [{ x: ta.x, y: sa.y }];
  } else {
    bends = [{ x: sa.x, y: ta.y }];
  }

  return simplifyOrthogonal([source, sa, ...bends, ta, target]);
}

export function segmentOrientation(a: Pt, b: Pt): "h" | "v" {
  return Math.abs(b.x - a.x) >= Math.abs(b.y - a.y) ? "h" : "v";
}

/** Insert corrective corners around the first/last waypoints so both ends stay
 *  orthogonal after a connected node has moved. The bend is *inserted*, never
 *  written over the waypoint, so the user's chosen bend survives. `skipSource` /
 *  `skipTarget` leave a junction end alone — it accepts a diagonal fan-in. */
export function rectifyEndpointWaypoints(
  source: Pt,
  sourceSide: Position,
  target: Pt,
  targetSide: Position,
  waypoints: readonly Pt[],
  options?: { skipSource?: boolean; skipTarget?: boolean },
): Pt[] {
  if (waypoints.length === 0) return [];
  const out: Pt[] = waypoints.map((w) => ({ ...w }));
  const sHoriz = sourceSide === Position.Left || sourceSide === Position.Right;
  const tHoriz = targetSide === Position.Left || targetSide === Position.Right;

  if (!options?.skipSource) {
    const first = out[0];
    if (sHoriz && first.y !== source.y) {
      out.unshift({ x: first.x, y: source.y });
    } else if (!sHoriz && first.x !== source.x) {
      out.unshift({ x: source.x, y: first.y });
    }
  }

  if (!options?.skipTarget) {
    const last = out[out.length - 1];
    if (tHoriz && last.y !== target.y) {
      out.push({ x: last.x, y: target.y });
    } else if (!tHoriz && last.x !== target.x) {
      out.push({ x: target.x, y: last.y });
    }
  }

  return out;
}

/** Whether stored waypoints are bad enough to discard. Both tests must pass —
 *  longer in proportion *and* in pixels — so a nudge doesn't churn hand-placed
 *  bends. */
export function shouldPruneWaypoints(
  source: Pt,
  target: Pt,
  waypoints: readonly Pt[],
): boolean {
  if (waypoints.length === 0) return false;
  const direct =
    Math.abs(target.x - source.x) + Math.abs(target.y - source.y);
  const polyline: Pt[] = [source, ...waypoints, target];
  let total = 0;
  for (let i = 0; i < polyline.length - 1; i++) {
    total +=
      Math.abs(polyline[i + 1].x - polyline[i].x) +
      Math.abs(polyline[i + 1].y - polyline[i].y);
  }
  const slack = total - direct;
  return (
    total > GEOMETRY.WAYPOINT_PRUNE_RATIO * direct &&
    slack > GEOMETRY.WAYPOINT_PRUNE_SLACK_PX
  );
}

/** Snap a dragged segment onto another parallel segment of the same cable
 *  within `threshold`, so two opposing bends fuse into one straight run.
 *  Returns `perp` unchanged when nothing is near. */
export function findOwnParallelSnap(
  poly: readonly Pt[],
  aIdx: number,
  orientation: "h" | "v",
  axisStart: number,
  axisEnd: number,
  perp: number,
  threshold = 6,
): number {
  if (poly.length < 4) return perp;
  let best = perp;
  let bestDist = threshold;
  const a0 = Math.min(axisStart, axisEnd);
  const a1 = Math.max(axisStart, axisEnd);
  for (let i = 0; i < poly.length - 1; i++) {
    if (i === aIdx) continue;
    const p = poly[i];
    const q = poly[i + 1];
    const dx = q.x - p.x;
    const dy = q.y - p.y;
    if (dx === 0 && dy === 0) continue;
    const horiz = Math.abs(dx) >= Math.abs(dy);
    if ((orientation === "h") !== horiz) continue;
    const otherPerp = orientation === "h" ? (p.y + q.y) / 2 : (p.x + q.x) / 2;
    const otherA0 =
      orientation === "h" ? Math.min(p.x, q.x) : Math.min(p.y, q.y);
    const otherA1 =
      orientation === "h" ? Math.max(p.x, q.x) : Math.max(p.y, q.y);
    if (Math.max(a0, otherA0) > Math.min(a1, otherA1) + 4) continue;
    const dist = Math.abs(otherPerp - perp);
    if (dist < bestDist) {
      bestDist = dist;
      best = otherPerp;
    }
  }
  return best;
}

/** Drop coincident or collinear points, including a reversal that draws as a
 *  hairpin once rounded. First and last points are always preserved. */
export function simplifyOrthogonal(points: Pt[], eps = 0.5): Pt[] {
  if (points.length <= 2) return points.slice();
  const out: Pt[] = [points[0]];
  for (let i = 1; i < points.length - 1; i++) {
    const prev = out[out.length - 1];
    const curr = points[i];
    const next = points[i + 1];
    if (Math.hypot(curr.x - prev.x, curr.y - prev.y) <= eps) continue;
    const cross =
      (curr.x - prev.x) * (next.y - curr.y) -
      (curr.y - prev.y) * (next.x - curr.x);
    if (Math.abs(cross) <= eps) continue;
    out.push(curr);
  }
  const last = points[points.length - 1];
  const prev = out[out.length - 1];
  if (Math.hypot(last.x - prev.x, last.y - prev.y) > eps) out.push(last);
  else out[out.length - 1] = last;
  return out;
}

/** SVG path through `points`, each corner radius clamped to half the shorter
 *  adjacent segment. A corner pair sharing a segment under `2 × radius` merges
 *  into one cubic — two clamped quadratics that close together read as kinks. */
export function roundedPath(points: Pt[], radius: number): string {
  if (points.length < 2) return "";
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }
  const segLen: number[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    segLen.push(
      Math.hypot(points[i + 1].x - points[i].x, points[i + 1].y - points[i].y) ||
        1,
    );
  }
  const unit = (a: Pt, b: Pt, len: number): Pt => ({
    x: (b.x - a.x) / len,
    y: (b.y - a.y) / len,
  });
  let d = `M ${points[0].x} ${points[0].y}`;
  let i = 1;
  while (i < points.length - 1) {
    const prev = points[i - 1];
    const curr = points[i];
    const next = points[i + 1];
    const d1 = segLen[i - 1];
    const d2 = segLen[i];
    if (i + 2 < points.length && d2 < radius * 2) {
      const after = points[i + 2];
      const d3 = segLen[i + 1];
      const r1 = Math.min(radius, d1 / 2);
      const r3 = Math.min(radius, d3 / 2);
      const u1 = unit(prev, curr, d1);
      const u3 = unit(next, after, d3);
      const start = { x: curr.x - u1.x * r1, y: curr.y - u1.y * r1 };
      const end = { x: next.x + u3.x * r3, y: next.y + u3.y * r3 };
      d += ` L ${start.x} ${start.y} C ${curr.x} ${curr.y} ${next.x} ${next.y} ${end.x} ${end.y}`;
      i += 2;
      continue;
    }
    const r = Math.min(radius, d1 / 2, d2 / 2);
    const u1 = unit(prev, curr, d1);
    const u2 = unit(curr, next, d2);
    const start = { x: curr.x - u1.x * r, y: curr.y - u1.y * r };
    const end = { x: curr.x + u2.x * r, y: curr.y + u2.y * r };
    d += ` L ${start.x} ${start.y} Q ${curr.x} ${curr.y} ${end.x} ${end.y}`;
    i++;
  }
  const last = points[points.length - 1];
  d += ` L ${last.x} ${last.y}`;
  return d;
}

/** Split a polyline's waypoints at `fraction` of the arc length, so each half of
 *  a split cable keeps the routing the user shaped. The split point itself is
 *  *not* materialised — the caller anchors it on a junction node. */
export function splitWaypointsAtFraction(
  source: Pt,
  target: Pt,
  waypoints: readonly Pt[],
  fraction: number,
): { before: Pt[]; after: Pt[] } {
  if (waypoints.length === 0) return { before: [], after: [] };
  const f = Math.max(0, Math.min(1, fraction));
  const points: Pt[] = [source, ...waypoints, target];
  const lengths: number[] = [];
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const dx = points[i + 1].x - points[i].x;
    const dy = points[i + 1].y - points[i].y;
    const len = Math.hypot(dx, dy);
    lengths.push(len);
    total += len;
  }
  if (total === 0) return { before: [], after: waypoints.map((p) => ({ ...p })) };
  const cutoff = total * f;
  const before: Pt[] = [];
  const after: Pt[] = [];
  let cum = 0;
  for (let i = 1; i < points.length - 1; i++) {
    cum += lengths[i - 1];
    const wp = points[i];
    if (cum < cutoff) before.push({ ...wp });
    else after.push({ ...wp });
  }
  return { before, after };
}
